/**
 * Pull the real error message out of a Supabase Edge Function failure.
 *
 * supabase.functions.invoke rejects a non-2xx response with a FunctionsHttpError
 * whose .message is the unhelpful "Edge Function returned a non-2xx status code".
 * The function's actual body lives on .context (a Response). It may be JSON
 * (e.g. { error: "Assign a Sender ..." } from the proposal functions) or plain
 * text (e.g. the PDF generators), so read the text once and parse JSON if it is
 * JSON, otherwise use the raw text, falling back to a caller-supplied message.
 */
export async function fnErrorMessage(err: unknown, fallback = 'Request failed'): Promise<string> {
  const ctx = (err as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.text === 'function') {
    try {
      const raw = (await ctx.clone().text()).trim();
      if (raw) {
        try {
          const body = JSON.parse(raw);
          const real = body?.error || body?.message;
          if (real) return String(real);
        } catch {
          // Body was not JSON, use the raw text below.
        }
        return raw;
      }
    } catch {
      // Body unreadable, fall through to the message/fallback below.
    }
  }
  const msg = (err as { message?: string } | null)?.message;
  if (msg && !/non-2xx status code/i.test(msg)) return msg;
  return fallback;
}
